import { isAxiosError } from 'axios';
import type { TokenResponse } from '@/core/generated/api';
import {
    clearTokens,
    getAccessToken,
    getRefreshToken,
    getRoleCode,
    getUserId,
    setTokens,
} from '@/core/utils/tokenUtils';

export type SessionTokens = TokenResponse & { accessToken: string; refreshToken: string };

/** Сообщает о завершении авторизационной сессии */
export class SessionExpiredError extends Error {
    constructor() {
        super('Сессия истекла. Пожалуйста, войдите заново.');
    }
}

/** Сообщает о смене сессии во время запроса */
export class SessionChangedError extends Error {
    constructor() {
        super('Сессия изменилась. Повторите действие.');
    }
}

let pendingRefresh: {
    token: string | null;
    userId: string | null;
    promise: Promise<SessionTokens>;
} | null = null;

/** Возвращает сохранённую сессию сотрудника */
const readTokens = (): SessionTokens => {
    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();

    if (!accessToken || !refreshToken) throw new SessionChangedError();

    return {
        accessToken,
        refreshToken,
        userId: getUserId(),
        login: localStorage.getItem('login'),
        role: localStorage.getItem('role'),
        roleCode: getRoleCode() ?? undefined,
    };
};

/** Завершает отклонённую сервером сессию */
export const expireSession = (refreshToken: string | null): never => {
    if (getRefreshToken() !== refreshToken) throw new SessionChangedError();

    clearTokens();

    throw new SessionExpiredError();
};

/** Обновляет токены сотрудника после проверки текущей сессии */
const rotateTokens = async (
    refreshToken: string | null,
    userId: string | null
): Promise<SessionTokens> => {
    if (getUserId() !== userId) throw new SessionChangedError();
    if (getRefreshToken() !== refreshToken) return readTokens();
    if (!refreshToken) return expireSession(refreshToken);

    try {
        const { getAuthApi } = await import('./generatedApiClient');

        const response = await getAuthApi()
            .apiV1AuthRefreshPost({
                refreshTokenRequest: {
                    refreshToken,
                    deviceToken: 'manage-web',
                },
            }, { timeout: 15_000 });

        if (getUserId() !== userId) throw new SessionChangedError();
        if (getRefreshToken() !== refreshToken) return readTokens();
        if (response.status === 204) return expireSession(refreshToken);

        const tokens = response.data;

        if (!tokens?.accessToken || !tokens.refreshToken) {
            throw new Error('Не удалось обновить сессию. Повторите попытку.');
        }

        setTokens(tokens.accessToken, tokens.refreshToken, tokens.userId, tokens.login, tokens.role, tokens.roleCode);

        return {
            ...tokens,
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken,
        };
    } catch (error: unknown) {
        if (error instanceof SessionExpiredError || error instanceof SessionChangedError) throw error;
        if (getUserId() !== userId) throw new SessionChangedError();
        if (getRefreshToken() !== refreshToken) return readTokens();

        const status = isAxiosError(error)
            ? error.response?.status
            : undefined;

        if (status === 400 || status === 401 || status === 403) expireSession(refreshToken);

        throw error;
    }
};

/** Продлевает общую сессию сотрудника во вкладках админки */
export const refreshSession = (): Promise<SessionTokens> => {
    const refreshToken = getRefreshToken();
    const userId = getUserId();
    const isCurrentRefresh = pendingRefresh?.token === refreshToken && pendingRefresh.userId === userId;

    if (pendingRefresh && isCurrentRefresh) return pendingRefresh.promise;

    /** Запрашивает продление текущей сессии */
    const refresh = async (): Promise<SessionTokens> => {
        if (!navigator.locks) {
            throw new Error('Не удалось безопасно обновить сессию. Обновите браузер и повторите попытку.');
        }

        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 30_000);

        try {
            return await navigator.locks.request('vox:auth-refresh', { signal: controller.signal }, () => {
                clearTimeout(timeout);

                return rotateTokens(refreshToken, userId);
            });
        } finally {
            clearTimeout(timeout);
        }
    };

    const promise = refresh()
        .finally(() => {
            if (pendingRefresh?.promise === promise) pendingRefresh = null;
        });

    pendingRefresh = {
        token: refreshToken,
        userId,
        promise,
    };

    return promise;
};
